import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack, Edit, Cancel, CheckCircle } from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Sales Contract 360: header + lifecycle ribbon, contracted rates, and the
// Activate/Cancel workflow actions. Mirrors RfqDetailPage.tsx's overall
// shape.
// ---------------------------------------------------------------------------

type ContractStatus = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'EXPIRED', label: 'Expired' },
];

const FLOW_INDEX: Partial<Record<ContractStatus, number>> = {
  DRAFT: 0,
  ACTIVE: 1,
  EXPIRED: 2,
};

const STATUS_TONE: Record<ContractStatus, StatusTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

interface ContractItem {
  id?: number;
  product_id: string | number | null;
  product_name?: string | null;
  contract_rate: number | string;
  min_quantity: number | string | null;
  max_quantity: number | string | null;
  uom: string | null;
}

interface ContractDetail {
  id: number;
  contract_number: string;
  customer_id: string | number;
  customer_name?: string;
  contract_date: string;
  valid_from: string | null;
  valid_to: string | null;
  payment_terms: string | null;
  credit_days: number | string | null;
  delivery_terms: string | null;
  price_escalation: string | null;
  currency: string | null;
  notes: string | null;
  status: ContractStatus;
  items: ContractItem[];
}

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const SalesContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { contract: data } = await api.get<{ contract: ContractDetail }>(`/sales-contracts/${id}`);
      setContract(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/contracts')} sx={{ mb: 2 }}>
          Back to Contracts
        </Button>
        <Alert severity="error">{error || 'Contract not found'}</Alert>
      </Box>
    );
  }

  const isSideState = contract.status === 'CANCELLED';
  const flowIndex = FLOW_INDEX[contract.status] ?? 0;

  const canEdit = contract.status === 'DRAFT';
  const canActivate = contract.status === 'DRAFT';
  const canCancel = contract.status === 'DRAFT' || contract.status === 'ACTIVE';
  const noActionsAvailable = !canEdit && !canActivate && !canCancel;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = () =>
    runAction('activate', async () => {
      await api.post(`/sales-contracts/${id}/activate`);
      setNotice('Contract activated.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await api.post(`/sales-contracts/${id}/cancel`, { reason: cancelReason.trim() || undefined });
      setCancelOpen(false);
      setCancelReason('');
      setNotice('Contract cancelled.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/contracts')} sx={{ mb: 1 }}>
        Back to Contracts
      </Button>

      <PageHeader
        title={contract.contract_number}
        qualifier={contract.customer_name ? `· ${contract.customer_name}` : undefined}
        subtitle={`Raised ${fmtDate(contract.contract_date)}${contract.valid_to ? ` · Valid ${fmtDate(contract.valid_from)} – ${fmtDate(contract.valid_to)}` : ''}`}
        actions={<StatusDot label={contract.status} tone={STATUS_TONE[contract.status]} />}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity="warning" icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>Contract Cancelled</Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="outlined" startIcon={<Edit />} onClick={() => navigate(`/sales/contracts/${id}/edit`)}>
              Edit
            </Button>
          )}
          {canActivate && (
            <Button variant="contained" color="success" startIcon={<CheckCircle />} disabled={actionLoading !== null} onClick={handleActivate}>
              {actionLoading === 'activate' ? 'Activating...' : 'Activate'}
            </Button>
          )}
          {canCancel && (
            <Button variant="outlined" color="error" startIcon={<Cancel />} disabled={actionLoading !== null} onClick={() => setCancelOpen(true)}>
              Cancel Contract
            </Button>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This contract is {contract.status.toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Contract Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Customer', contract.customer_name],
                ['Currency', contract.currency],
                ['Payment Terms', contract.payment_terms],
                ['Credit Days', contract.credit_days !== null && contract.credit_days !== undefined ? String(contract.credit_days) : null],
                ['Delivery Terms', contract.delivery_terms],
                ['Price Escalation', contract.price_escalation],
                ['Valid From', fmtDate(contract.valid_from)],
                ['Valid To', fmtDate(contract.valid_to)],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Notes
            </Typography>
            <Typography variant="body2" color={contract.notes ? 'text.primary' : 'text.disabled'} sx={{ whiteSpace: 'pre-wrap' }}>
              {contract.notes || 'No notes recorded.'}
            </Typography>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Contracted Rates
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'Contract Rate', 'Min Qty', 'Max Qty', 'UOM'].map((h, i) => (
                      <TableCell key={h} align={i === 1 || i === 2 || i === 3 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contract.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <TableEmptyState title="No contracted rates on this contract" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    contract.items.map((item, idx) => (
                      <TableRow key={item.id ?? idx} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>{item.product_name || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatIndianNumber(toNum(item.contract_rate))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.min_quantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {item.max_quantity !== null && item.max_quantity !== undefined ? toNum(item.max_quantity) : '—'}
                        </TableCell>
                        <TableCell>{item.uom || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>
      </Grid>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Contract</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>This cannot be undone. Sales orders can no longer be raised against this contract.</DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Contract'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesContractDetailPage;
